/**
 * File preview with options component for upload dialog
 * Combines file preview with type selection and certified copy option
 */

import { createElement } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import {
  formatFileSize,
  getFileIcon,
  detectFileTypeCategory
} from '@/lib/utils/document-utils';
import { FileTypeSelector } from './file-type-selector';
import type { UploadFile } from '@/hooks/use-file-upload';

export interface FilePreviewWithOptionsProps {
  file: UploadFile;
  onRemove: (fileId: string) => void;
  onFileTypeChange: (fileId: string, fileType: string) => void;
  onCertifiedCopyChange: (fileId: string, isCertified: boolean) => void;
  disabled?: boolean;
  excludeCrucial?: boolean;
  onlyCrucial?: boolean;
  hideFileTypeSelector?: boolean;
}

export function FilePreviewWithOptions({
  file,
  onRemove,
  onFileTypeChange,
  onCertifiedCopyChange,
  disabled,
  excludeCrucial = false,
  onlyCrucial = false,
  hideFileTypeSelector = false
}: FilePreviewWithOptionsProps) {
  const fileCategory = detectFileTypeCategory(file.file.name);
  const iconComponent = getFileIcon(
    fileCategory !== 'other' ? fileCategory : file.fileType || 'OTHER'
  );

  const getStatusBadge = () => {
    switch (file.status) {
      case 'uploading':
        return <StatusBadge tone="neutral">Uploading...</StatusBadge>;
      case 'success':
        return <StatusBadge tone="success">Uploaded</StatusBadge>;
      case 'error':
        return <StatusBadge tone="critical">Failed</StatusBadge>;
      default:
        return null;
    }
  };

  const getStatusIcon = () => {
    switch (file.status) {
      case 'success':
        return (
          <CheckCircle className="text-success-subtle-foreground h-4 w-4" />
        );
      case 'error':
        return <AlertCircle className="text-destructive h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <Card className={file.status === 'error' ? 'border-destructive' : ''}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* File info header */}
          <div className="flex items-start gap-3">
            {createElement(iconComponent, {
              className: 'text-muted-foreground h-8 w-8 shrink-0'
            })}

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {file.file.name}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {formatFileSize(file.file.size)}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusIcon()}
                  {getStatusBadge()}
                  {!disabled && file.status !== 'uploading' && (
                    <Button
                      variant="ghost"
                      size="icon-2xs"
                      onClick={() => onRemove(file.id)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Remove file</span>
                    </Button>
                  )}
                </div>
              </div>

              {file.status === 'uploading' && (
                <Progress value={file.progress} className="mt-2 h-1" />
              )}

              {file.error && (
                <p className="text-destructive mt-1 text-xs">{file.error}</p>
              )}
            </div>
          </div>

          {/* File options */}
          {file.status !== 'uploading' && file.status !== 'success' && (
            <div className="border-t pt-3">
              {!hideFileTypeSelector && (
                <div className="mb-3">
                  <FileTypeSelector
                    value={file.fileType || ''}
                    onChange={(value) => onFileTypeChange(file.id, value)}
                    fileId={file.id}
                    disabled={disabled}
                    excludeCrucial={excludeCrucial}
                    onlyCrucial={onlyCrucial}
                  />
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`certified-${file.id}`}
                  checked={file.isCertifiedCopy || false}
                  onCheckedChange={(checked) =>
                    onCertifiedCopyChange(file.id, checked as boolean)
                  }
                  disabled={disabled}
                />
                <Label
                  htmlFor={`certified-${file.id}`}
                  className="cursor-pointer text-sm font-normal"
                >
                  This is a certified copy
                </Label>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
